import { ScenePreview } from "@cva/renderer";
import {
  BRAND_LOGO_LABELS,
  type BrandAsset,
  brandScale,
  brandTypography,
  buildParentMap,
  elementLabel,
  moveElementTo,
  pageSize,
  type SceneElement,
  supportsBoard,
} from "@cva/shared";
import { Link } from "@tanstack/react-router";
import {
  Blocks,
  Captions,
  ChartColumn,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  FileImage,
  Folder,
  Images,
  Layers,
  Lock,
  LockOpen,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Shapes,
  Smile,
  Square,
  Table2,
  Trash2,
  Type,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { ContextMenuPopup, useContextMenu } from "../components/ui/context-menu";
import { DropdownItem, DropdownLabel, DropdownSeparator } from "../components/ui/dropdown";
import { useOrgRole } from "../lib/org";
import { cn } from "../lib/utils";
import { BlocksPanel } from "./BlocksPanel";
import { NoBrandKitHint } from "./BrandHint";
import { CanvasContextMenu } from "./ContextMenu";
import {
  addAsset,
  addPageAfter,
  applyBrandColor,
  applyBrandTextStyle,
  duplicateCurrentPage,
  movePageTo,
  removePageAt,
  toggleHidden,
} from "./commands";
import { ElementsPanel } from "./ElementsPanel";
import { useEnv } from "./env";
import { type BlocksView, LEFT_TABS, type LeftTab, useLeftNav } from "./left-nav";
import { MediaLibraryPanel } from "./MediaLibrary";
import { NameDialog } from "./NameDialog";
import { NewPageDialog } from "./PageSettings";
import { PanelSwitchContext, PanelTitle } from "./PanelTitle";
import { useEditor, useEditorApi } from "./store";
import { LayoutsPanel } from "./video/LayoutsPanel";
import { useVideoApi } from "./video/store";

const TAB_INFO: Record<LeftTab, { label: string; icon: ReactNode }> = {
  media: { label: "Medien", icon: <Images /> },
  elements: { label: "Elemente", icon: <Shapes /> },
  blocks: { label: "Bausteine", icon: <Blocks /> },
  brand: { label: "Marke", icon: <Palette /> },
  pages: { label: "Seiten", icon: <Copy /> },
  layers: { label: "Ebenen", icon: <Layers /> },
};

/** Tabs about the document's structure: below the ones that add content. */
const STRUCTURE_TABS: LeftTab[] = ["pages", "layers"];

function RailButton({ id }: { id: LeftTab }) {
  const active = useLeftNav((s) => s.tab === id);
  const toggle = useLeftNav((s) => s.toggle);
  const info = TAB_INFO[id];
  return (
    <button
      type="button"
      onClick={() => toggle(id)}
      aria-pressed={active}
      className={cn(
        "flex w-14 flex-col items-center gap-1 rounded-lg py-2 text-[11px] text-zinc-600 hover:bg-zinc-100 [&_svg]:size-5",
        active && "bg-brand-50 text-brand-700 hover:bg-brand-50",
      )}
    >
      {info.icon}
      {info.label}
    </button>
  );
}

export function LeftRail() {
  const kind = useLeftNav((s) => s.kind);
  const collapsed = useLeftNav((s) => s.tab === null);
  const collapse = useLeftNav((s) => s.collapse);
  const expand = useLeftNav((s) => s.expand);
  const tabs = LEFT_TABS[kind];
  return (
    <nav className="flex w-16 shrink-0 flex-col items-center gap-1 border-r border-zinc-200 bg-white py-2">
      {tabs
        .filter((id) => !STRUCTURE_TABS.includes(id))
        .map((id) => (
          <RailButton key={id} id={id} />
        ))}
      <div className="flex-1" />
      <div className="mb-1 h-px w-8 bg-zinc-200" />
      {tabs
        .filter((id) => STRUCTURE_TABS.includes(id))
        .map((id) => (
          <RailButton key={id} id={id} />
        ))}
      <button
        type="button"
        onClick={() => (collapsed ? expand() : collapse())}
        title={collapsed ? "Seitenleiste ausklappen" : "Seitenleiste einklappen – mehr Platz"}
        aria-label={collapsed ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}
        className="mt-1 flex size-9 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 [&_svg]:size-4"
      >
        {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
      </button>
    </nav>
  );
}

export function LeftPanel() {
  const tab = useLeftNav((s) => s.tab);
  const collapse = useLeftNav((s) => s.collapse);
  if (!tab) return null;
  return (
    <div className="relative flex shrink-0">
      <aside className="flex w-72 shrink-0 flex-col overflow-hidden border-r border-zinc-200 bg-white">
        {tab === "media" ? <MediaLibraryPanel /> : null}
        {tab === "elements" ? <ElementsPanel /> : null}
        {tab === "blocks" ? <BlocksTab /> : null}
        {tab === "brand" ? <BrandPanel /> : null}
        {tab === "pages" ? <PagesPanel /> : null}
        {tab === "layers" ? <LayersPanel /> : null}
      </aside>
      {/* Grip on the panel's edge, as in other design tools. */}
      <button
        type="button"
        onClick={collapse}
        title="Seitenleiste einklappen"
        aria-label="Seitenleiste einklappen"
        className="absolute top-1/2 -right-3 z-20 flex h-14 w-3 -translate-y-1/2 items-center justify-center rounded-r-md border border-l-0 border-zinc-200 bg-white text-zinc-400 shadow-sm hover:text-zinc-700"
      >
        <ChevronLeft className="size-3" />
      </button>
    </div>
  );
}

/** „Bausteine“: ready-made blocks and (videos) screen layouts in one tab. */
function BlocksTab() {
  const video = useVideoApi();
  const view = useLeftNav((s) => s.blocksView);
  const setView = useLeftNav((s) => s.setBlocksView);
  const options: { id: BlocksView; label: string }[] = video
    ? [
        { id: "blocks", label: "Bausteine" },
        { id: "layouts", label: "Layouts" },
      ]
    : [{ id: "blocks", label: "Bausteine" }];
  const current = options.some((option) => option.id === view) ? view : "blocks";
  return (
    <PanelSwitchContext.Provider
      value={{ options, value: current, onChange: (id) => setView(id as BlocksView) }}
    >
      {current === "blocks" ? <BlocksPanel /> : null}
      {current === "layouts" ? <LayoutsPanel /> : null}
    </PanelSwitchContext.Provider>
  );
}

function brandAssetInfo(asset: BrandAsset) {
  return {
    id: asset.id,
    kind: asset.kind,
    url: asset.url,
    thumbnailUrl: asset.thumbnailUrl,
    width: asset.width,
    height: asset.height,
    name: asset.name,
  };
}

function BrandAssetTile({ asset, label }: { asset: BrandAsset; label: string }) {
  const store = useEditorApi();
  const readOnly = useEditor((s) => s.readOnly);
  return (
    <button
      type="button"
      title={`${label} einfügen`}
      draggable={!readOnly}
      onDragStart={(event) => {
        event.dataTransfer.setData("application/x-cva-asset", asset.id);
        event.dataTransfer.effectAllowed = "copy";
      }}
      onClick={() => !readOnly && addAsset(store, brandAssetInfo(asset))}
      className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] p-2 hover:ring-2 hover:ring-brand-400"
    >
      <img
        src={asset.thumbnailUrl ?? asset.url}
        alt={label}
        className="max-h-full max-w-full object-contain"
        draggable={false}
      />
    </button>
  );
}

function BrandPanel() {
  const store = useEditorApi();
  const env = useEnv();
  const { can } = useOrgRole(env.orgId);
  const readOnly = useEditor((s) => s.readOnly);
  const brand = useEditor((s) => s.brand);
  const hasSelection = useEditor((s) => s.selection.length > 0);
  const scale = useEditor((s) => brandScale(s.doc));
  const kit = env.brandKit;
  if (env.brandMissing) {
    return (
      <>
        <PanelTitle>Marke</PanelTitle>
        <div className="p-4">
          <div className="space-y-1.5 rounded-lg bg-zinc-50 p-3 text-xs text-zinc-600">
            <p className="font-medium text-zinc-800">Noch kein Brand Kit</p>
            <p>
              Mit einem Brand Kit hast du hier die Farben, Textstile, Logos und Grafiken deiner
              Marke mit einem Klick zur Hand.
            </p>
            <NoBrandKitHint />
          </div>
        </div>
      </>
    );
  }
  if (!kit || !brand) return <p className="p-4 text-sm text-zinc-500">Brand Kit wird geladen …</p>;
  return (
    <>
      <PanelTitle
        actions={
          can("brand.update") ? (
            <Link
              to="/o/$orgId/brand"
              params={{ orgId: env.orgId }}
              search={{ kit: kit.id }}
              className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-medium text-brand-700 hover:bg-brand-50"
            >
              <Pencil className="size-3.5" /> Bearbeiten
            </Link>
          ) : null
        }
      >
        {kit.name}
      </PanelTitle>
      <div className="space-y-5 overflow-y-auto p-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-zinc-700">Farben</p>
          <div className="grid grid-cols-5 gap-2">
            {brand.colors.map((color) => (
              <button
                key={color.key}
                type="button"
                disabled={readOnly}
                title={`${color.name} · ${color.color.toUpperCase()}${color.cmyk ? ` · CMYK ${color.cmyk.join("/")}` : ""}`}
                onClick={() => applyBrandColor(store, color.key)}
                className="aspect-square rounded-lg ring-1 ring-black/10 ring-inset transition hover:scale-105 disabled:hover:scale-100"
                style={{ background: color.color }}
              />
            ))}
          </div>
          <p className="text-xs text-zinc-400">
            {hasSelection
              ? "Klick färbt die Auswahl ein und verknüpft sie mit der Marke."
              : "Ohne Auswahl färbt ein Klick den Seitenhintergrund."}
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold text-zinc-700">Textstile</p>
          {brand.textStyles.map((style) => {
            const typography = brandTypography(style, 1);
            const color = brand.colors.find((entry) => entry.key === style.color)?.color;
            return (
              <button
                key={style.key}
                type="button"
                disabled={readOnly}
                onClick={() => applyBrandTextStyle(store, style.key, style.name)}
                title={`${style.fontFamily} · ${Math.round(style.fontSize * scale)} px in diesem Format`}
                className="block w-full truncate rounded-lg border border-zinc-200 px-3 py-2 text-left hover:border-brand-300 hover:bg-brand-50"
                style={{
                  fontFamily: `"${typography.fontFamily}", Inter, sans-serif`,
                  fontWeight: typography.fontWeight,
                  fontStyle: typography.italic ? "italic" : undefined,
                  fontSize: Math.min(26, Math.max(13, style.fontSize * 0.3)),
                  letterSpacing: `${typography.letterSpacing}em`,
                  textTransform: typography.uppercase ? "uppercase" : undefined,
                  lineHeight: 1.2,
                  color,
                }}
              >
                {style.name}
              </button>
            );
          })}
        </div>
        {kit.logos.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-zinc-700">Logos</p>
            <div className="grid grid-cols-2 gap-2">
              {kit.logos.map((logo) => (
                <BrandAssetTile
                  key={logo.variant}
                  asset={logo.asset}
                  label={BRAND_LOGO_LABELS[logo.variant]}
                />
              ))}
            </div>
          </div>
        ) : null}
        {kit.graphics.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-zinc-700">Grafiken</p>
            <div className="grid grid-cols-3 gap-2">
              {kit.graphics.map((asset) => (
                <BrandAssetTile key={asset.id} asset={asset} label={asset.name} />
              ))}
            </div>
          </div>
        ) : null}
        {kit.logos.length === 0 && kit.graphics.length === 0 && can("brand.update") ? (
          <p className="rounded-lg bg-zinc-50 p-3 text-xs text-zinc-500">
            Hinterlege Logos und Grafiken im{" "}
            <Link
              to="/o/$orgId/brand"
              params={{ orgId: env.orgId }}
              search={{ kit: kit.id }}
              className="font-medium text-brand-700 hover:underline"
            >
              Brand Kit
            </Link>
            , dann lassen sie sich hier mit einem Klick einfügen.
          </p>
        ) : null}
      </div>
    </>
  );
}

function PagesPanel() {
  const store = useEditorApi();
  const env = useEnv();
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const readOnly = useEditor((s) => s.readOnly);
  const menu = useContextMenu<number>();
  const target = menu.target;
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [renameId, setRenameId] = useState<string | null>(null);
  const renamePage = doc.pages.find((page) => page.id === renameId);
  const own = supportsBoard(doc);
  return (
    <>
      <PanelTitle
        actions={
          readOnly ? null : (
            <span className="flex items-center">
              <button
                type="button"
                onClick={() => addPageAfter(store)}
                className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-medium text-brand-700 hover:bg-brand-50"
              >
                <Plus className="size-3.5" /> Seite
              </button>
              {own ? (
                <button
                  type="button"
                  title="Seite mit anderer Größe"
                  aria-label="Seite mit anderer Größe"
                  onClick={() => setNewPageOpen(true)}
                  className="inline-flex h-8 items-center rounded-md px-1.5 text-brand-700 hover:bg-brand-50"
                >
                  <ChevronDown className="size-3.5" />
                </button>
              ) : null}
            </span>
          )
        }
      >
        Seiten
      </PanelTitle>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {doc.pages.map((page, index) => (
          <div key={page.id} className="group">
            <button
              type="button"
              onClick={() => {
                store.getState().goToPage(index);
              }}
              onContextMenu={readOnly ? undefined : menu.open(index)}
              className={cn(
                "block w-full overflow-hidden rounded-lg border-2 bg-white transition",
                index === pageIndex
                  ? "border-brand-500"
                  : "border-transparent ring-1 ring-zinc-200 hover:ring-zinc-300",
              )}
            >
              <ScenePreview
                document={doc}
                pageIndex={index}
                width={236}
                resolveAsset={env.resolveAsset}
              />
            </button>
            <div className="mt-1 flex items-center justify-between text-xs text-zinc-500">
              <span>
                {doc.pages[index]?.name?.trim() || `Seite ${index + 1}`}
                {own ? (
                  <span className="text-zinc-400">
                    {" "}
                    · {pageSize(doc, page).width} × {pageSize(doc, page).height}
                  </span>
                ) : null}
              </span>
              {readOnly ? null : (
                <span className="flex opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
                  <button
                    type="button"
                    title="Seite umbenennen"
                    aria-label="Seite umbenennen"
                    className="rounded p-1 hover:bg-zinc-100"
                    onClick={() => setRenameId(page.id)}
                  >
                    <Pencil className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Nach oben"
                    disabled={index === 0}
                    className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30"
                    onClick={() => movePageTo(store, index, index - 1)}
                  >
                    <ChevronDown className="size-3.5 rotate-180" />
                  </button>
                  <button
                    type="button"
                    title="Nach unten"
                    disabled={index === doc.pages.length - 1}
                    className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30"
                    onClick={() => movePageTo(store, index, index + 1)}
                  >
                    <ChevronDown className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Duplizieren"
                    className="rounded p-1 hover:bg-zinc-100"
                    onClick={() => duplicateCurrentPage(store, index)}
                  >
                    <Copy className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Löschen"
                    disabled={doc.pages.length <= 1}
                    className="rounded p-1 hover:bg-zinc-100 hover:text-red-600 disabled:opacity-30"
                    onClick={() => removePageAt(store, index)}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <NameDialog
        open={Boolean(renamePage) && !readOnly}
        onOpenChange={(open) => {
          if (!open) setRenameId(null);
        }}
        title="Seite umbenennen"
        maxLength={100}
        initial={renamePage?.name ?? ""}
        onConfirm={(name) => {
          if (store.getState().readOnly) return;
          store.getState().update((draft) => {
            const page = draft.pages.find((page) => page.id === renameId);
            if (page) page.name = name;
          });
        }}
      />
      <NewPageDialog open={newPageOpen} onOpenChange={setNewPageOpen} />
      <ContextMenuPopup at={menu.at} onClose={menu.close}>
        {target !== null ? (
          <>
            <DropdownLabel>Seite {target + 1}</DropdownLabel>
            <DropdownItem onSelect={() => setRenameId(doc.pages[target]!.id)}>
              <Pencil /> Umbenennen
            </DropdownItem>
            <DropdownItem
              onSelect={() => {
                store.getState().setPage(target);
                addPageAfter(store);
              }}
            >
              <Plus /> Neue Seite danach
            </DropdownItem>
            <DropdownItem onSelect={() => duplicateCurrentPage(store, target)}>
              <Copy /> Duplizieren
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem
              disabled={target === 0}
              onSelect={() => movePageTo(store, target, target - 1)}
            >
              <ChevronDown className="rotate-180" /> Nach oben
            </DropdownItem>
            <DropdownItem
              disabled={target === doc.pages.length - 1}
              onSelect={() => movePageTo(store, target, target + 1)}
            >
              <ChevronDown /> Nach unten
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem
              danger
              disabled={doc.pages.length <= 1}
              onSelect={() => removePageAt(store, target)}
            >
              <Trash2 /> Seite löschen
            </DropdownItem>
          </>
        ) : null}
      </ContextMenuPopup>
    </>
  );
}

function LayerRow({
  id,
  depth,
  onMenu,
}: {
  id: string;
  depth: number;
  /** Right click: the element's menu, as on the canvas. */
  onMenu: (event: React.MouseEvent) => void;
}) {
  const store = useEditorApi();
  const env = useEnv();
  const element = useEditor((s) => s.doc.elements[id]);
  const selected = useEditor((s) => s.selection.includes(id));
  const readOnly = useEditor((s) => s.readOnly);
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  if (!element) return null;
  const assetName =
    element.type === "image" || element.type === "svg"
      ? env.assets.get(element.assetId)?.name
      : undefined;
  const label = element.name ?? elementLabel(element, assetName).replace(/[„“]/g, "");
  const children = element.type === "group" ? [...element.children].reverse() : [];
  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: layer rows are mouse targets, keyboard users use the canvas shortcuts */}
      <div
        draggable={!readOnly && !renaming}
        onDragStart={(event) => {
          event.dataTransfer.setData("application/x-cva-layer", id);
          event.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes("application/x-cva-layer")) event.preventDefault();
        }}
        onDrop={(event) => {
          event.preventDefault();
          const dragged = event.dataTransfer.getData("application/x-cva-layer");
          if (!dragged || dragged === id) return;
          const state = store.getState();
          const parents = buildParentMap(state.doc);
          const target = parents.get(id);
          const source = parents.get(dragged);
          const sameParent =
            target &&
            source &&
            target.kind === source.kind &&
            (target.kind === "page"
              ? source.kind === "page" && source.pageIndex === target.pageIndex
              : source.kind === "group" && source.groupId === target.groupId);
          if (!sameParent) return;
          state.update((draft) => {
            const list =
              target.kind === "page"
                ? draft.pages[target.pageIndex]!.children
                : (draft.elements[target.groupId] as Extract<SceneElement, { type: "group" }>)
                    .children;
            // Layers are shown top-first, lists are bottom-first: drop above = after in the list.
            moveElementTo(
              draft,
              dragged,
              list.indexOf(id) + (list.indexOf(dragged) > list.indexOf(id) ? 1 : 0),
            );
          });
        }}
        onClick={(event) => store.getState().select([id], event.shiftKey ? "toggle" : "replace")}
        onContextMenu={(event) => {
          event.preventDefault();
          if (!store.getState().selection.includes(id)) store.getState().select([id]);
          onMenu(event);
        }}
        onDoubleClick={() => !readOnly && setRenaming(true)}
        onMouseEnter={() => store.getState().setHover(id)}
        onMouseLeave={() => store.getState().setHover(null)}
        className={cn(
          "group flex h-8 cursor-default items-center gap-1 rounded-md pr-1 text-sm",
          selected ? "bg-brand-50 text-brand-900" : "text-zinc-700 hover:bg-zinc-100",
          element.hidden && "opacity-50",
        )}
        style={{ paddingLeft: 6 + depth * 14 }}
      >
        {element.type === "group" ? (
          <button
            type="button"
            className="rounded p-0.5 hover:bg-zinc-200"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="text-zinc-400 [&_svg]:size-3.5">
          {element.type === "text" ? (
            <Type />
          ) : element.type === "group" ? (
            <Folder />
          ) : element.type === "shape" ? (
            <Square />
          ) : element.type === "captions" ? (
            <Captions />
          ) : element.type === "icon" ? (
            <Smile />
          ) : element.type === "chart" ? (
            <ChartColumn />
          ) : element.type === "table" ? (
            <Table2 />
          ) : (
            <FileImage />
          )}
        </span>
        {renaming ? (
          <input
            // biome-ignore lint/a11y/noAutofocus: inline rename field
            autoFocus
            defaultValue={label}
            className="h-6 w-full rounded border border-brand-400 px-1 text-sm outline-none"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter") (event.currentTarget as HTMLInputElement).blur();
              if (event.key === "Escape") setRenaming(false);
            }}
            onBlur={(event) => {
              const name = event.currentTarget.value.trim();
              store.getState().update((draft) => {
                const target = draft.elements[id];
                if (target) target.name = name || undefined;
              });
              setRenaming(false);
            }}
          />
        ) : (
          <span className="min-w-0 flex-1 truncate">{label}</span>
        )}
        {readOnly ? null : (
          <span
            className={cn(
              "flex",
              !element.locked && !element.hidden && "opacity-0 group-hover:opacity-100",
            )}
          >
            <button
              type="button"
              title={element.hidden ? "Einblenden" : "Ausblenden"}
              className="rounded p-1 hover:bg-zinc-200"
              onClick={(event) => {
                event.stopPropagation();
                toggleHidden(store, [id]);
              }}
            >
              {element.hidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
            </button>
            <button
              type="button"
              title={element.locked ? "Entsperren" : "Sperren"}
              className="rounded p-1 hover:bg-zinc-200"
              onClick={(event) => {
                event.stopPropagation();
                store.getState().update((draft) => {
                  const target = draft.elements[id];
                  if (target) target.locked = !target.locked;
                });
              }}
            >
              {element.locked ? <Lock className="size-3.5" /> : <LockOpen className="size-3.5" />}
            </button>
          </span>
        )}
      </div>
      {open
        ? children.map((child) => (
            <LayerRow key={child} id={child} depth={depth + 1} onMenu={onMenu} />
          ))
        : null}
    </>
  );
}

function LayersPanel() {
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const page = doc.pages[pageIndex];
  const ids = [...(page?.children ?? [])].reverse();
  const readOnly = useEditor((s) => s.readOnly);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const onMenu = (event: React.MouseEvent) => {
    if (!readOnly) setMenuAt({ x: event.clientX, y: event.clientY });
  };
  return (
    <>
      <PanelTitle>Ebenen</PanelTitle>
      <div className="flex-1 overflow-y-auto p-2">
        {ids.length === 0 ? (
          <p className="p-3 text-sm text-zinc-500">Diese Seite ist noch leer.</p>
        ) : null}
        {ids.map((id) => (
          <LayerRow key={id} id={id} depth={0} onMenu={onMenu} />
        ))}
      </div>
      <p className="border-t border-zinc-100 p-3 text-xs text-zinc-400">
        Oben liegt vorne. Zum Umsortieren ziehen, Doppelklick zum Umbenennen, Rechtsklick für alle
        Aktionen.
      </p>
      <CanvasContextMenu at={menuAt} onClose={() => setMenuAt(null)} />
    </>
  );
}
